import type { FocusEvent, KeyboardEvent, ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Input } from '@/lib/base/forms/input/input';
import { cx } from '@/utils/cx';

import { InlineFieldError } from '../components/InlineFieldError';
import { hasMeaningfulTitle } from '../notes/noteKind';
import { NOOP_SAVE, useInlineFieldEdit } from './useInlineFieldEdit';

interface TaskTitleFieldProps {
  title: string;
  onSave?: (value: string) => Promise<void>;
  /**
   * Open the editor immediately on mount instead of rendering the read
   * heading. Used by the task slideout, where the read heading is never shown
   * (the record header carries the name) and the editor is summoned by the
   * header's "Edit task" action. Renders nothing until the editor is open, so
   * there is no one-frame flash of a heading.
   */
  forceEditing?: boolean;
  /** Called once the editor closes for any reason (save, blur, Esc). */
  onEditingEnd?: () => void;
}

const HEADING_CLASS = 'text-lg font-semibold text-fg-primary';
const MUTED_HEADING_CLASS = 'text-lg font-semibold text-fg-tertiary';
// Deliberately NO min-height reservation matching the edit-state Input:
// reserving it removed the grow on edit entry but made the read-state title
// block visibly too tall (product call: compact read state wins). Zero-shift
// is achieved from the other side instead — see EDIT_WRAPPER_CLASS.
const READ_AFFORDANCE_CLASS =
  'text-left rounded-md outline-brand hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2';
// Negative margins cancel the Input's chrome exactly, so entering edit shifts
// NOTHING: with the title typography forced on the input (text-lg, 28px line —
// see EDIT_INPUT_CLASS), the `sm`-size box is 28px line + py-2 (16px) = 44px
// at every breakpoint; `-my-2` (−16px) makes it contribute the read heading's
// exact 28px to layout, the box bleeding into the surrounding flex gaps.
// `-mx-3` likewise cancels the input's `px-3` so the TEXT stays on the exact
// spot the heading text occupied (the box bleeds into the panel padding).
const EDIT_WRAPPER_CLASS = '-my-2 -mx-3 flex flex-col gap-1';
// Match the read heading's typography inside the editor — without this the
// text visibly jumps from 18px semibold to the input's default 16px regular,
// and the height math above stops being breakpoint-constant. `sm:text-lg` is
// required alongside the base class: the size scale's root is
// `text-sm sm:text-md`, and tailwind-merge resolves responsive variants as a
// separate group — a bare `text-lg` only displaces the base `text-sm`,
// leaving `sm:text-md` to win at desktop widths.
const EDIT_INPUT_CLASS = 'text-lg sm:text-lg font-semibold';

export function TaskTitleField({
  title,
  onSave,
  forceEditing = false,
  onEditingEnd,
}: TaskTitleFieldProps): ReactElement | null {
  const { isEditing, isSaving, error, startEditing, cancelEditing, commit } =
    useInlineFieldEdit(onSave ?? NOOP_SAVE);
  const [draft, setDraft] = useState(title);
  const readAffordanceRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasEditingRef = useRef(false);
  // Only an explicit keyboard exit (Enter submit / Escape cancel) should pull
  // focus back to the read affordance. A blur commit means focus has already
  // moved to wherever the user tabbed/clicked — restoring it there would steal
  // focus from that destination.
  const restoreFocusOnCloseRef = useRef(false);
  // `forceEditing` opens the editor exactly once, on mount — never again after
  // the user closes it (the parent unmounts the field on close; this guard
  // covers the frame in between).
  const forcedOpenRef = useRef(false);

  const editable = !!onSave;
  const meaningful = hasMeaningfulTitle(title);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (wasEditingRef.current && !isEditing) {
      if (restoreFocusOnCloseRef.current) {
        readAffordanceRef.current?.focus();
      }
      restoreFocusOnCloseRef.current = false;
      onEditingEnd?.();
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, onEditingEnd]);

  const handleStartEditing = useCallback(() => {
    setDraft(title);
    startEditing();
  }, [title, startEditing]);

  useEffect(() => {
    if (forceEditing && editable && !forcedOpenRef.current) {
      forcedOpenRef.current = true;
      handleStartEditing();
    }
  }, [forceEditing, editable, handleStartEditing]);

  const handleCommit = useCallback(() => {
    const v = draft.trim();
    if (v === '' || v === (title ?? '').trim()) {
      cancelEditing();
      return;
    }
    void commit(v).then(outcome => {
      // Roll the draft back to the current server title ONLY on a real
      // rejection (the editor stays open showing the persisted value rather
      // than the rejected text). An `'ignored'` outcome means a save for this
      // edit is already in flight — e.g. Enter fires commit, then the blur it
      // triggers fires a second commit — so the draft must be left as the user
      // typed it; resetting it here would erase in-progress input even though
      // the first request is still on track to succeed.
      if (outcome === 'failed') {
        setDraft(title);
      }
    });
  }, [draft, title, cancelEditing, commit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        // Stop the key from bubbling to the enclosing task slideout (which
        // treats Enter/Escape as its own shortcuts) — this keypress belongs to
        // the title editor, mirroring TaskDescriptionEditor's handler.
        e.preventDefault();
        e.stopPropagation();
        restoreFocusOnCloseRef.current = true;
        handleCommit();
      } else if (e.key === 'Escape') {
        // Prevent the bubbled Escape from closing the whole slideout instead of
        // just cancelling title editing.
        e.preventDefault();
        e.stopPropagation();
        restoreFocusOnCloseRef.current = true;
        cancelEditing();
      }
    },
    [handleCommit, cancelEditing]
  );

  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLDivElement>) => {
      restoreFocusOnCloseRef.current = false;
      handleCommit();
    },
    [handleCommit]
  );

  if (isEditing) {
    return (
      <div
        className={EDIT_WRAPPER_CLASS}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        <Input
          ref={inputRef}
          aria-label='Title'
          value={draft}
          onChange={setDraft}
          inputClassName={EDIT_INPUT_CLASS}
          // Freeze the field while its save is in flight so a slower save can't
          // be overtaken by a newer keystroke that the in-flight commit would
          // then drop when it closes the editor (see the duplicate-commit
          // finding on useInlineFieldEdit).
          isReadOnly={isSaving}
        />
        <InlineFieldError message={error} />
      </div>
    );
  }

  // Summoned-editor mode renders nothing outside the editor: no read heading
  // before it opens, none after it closes.
  if (forceEditing) {
    return null;
  }

  if (!editable) {
    return meaningful ? <h2 className={HEADING_CLASS}>{title}</h2> : null;
  }

  if (meaningful) {
    return (
      <AriaButton
        ref={readAffordanceRef}
        aria-label='Edit title'
        onPress={handleStartEditing}
        className={cx(HEADING_CLASS, READ_AFFORDANCE_CLASS)}
      >
        {title}
      </AriaButton>
    );
  }

  return (
    <AriaButton
      ref={readAffordanceRef}
      aria-label='Add a title'
      onPress={handleStartEditing}
      className={cx(MUTED_HEADING_CLASS, READ_AFFORDANCE_CLASS)}
    >
      Add a title
    </AriaButton>
  );
}
